import React, { useEffect, useState } from "react";
import { UserProvider } from "./UserContext";
import { ThemeProvider } from "./ThemeContext";
import { useMediaQuery } from "react-responsive";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  useLocation,
} from "react-router-dom";
import ReactGA from "react-ga4";
import SignIn from "./SignIn/SignIn";
import Logout from "./Logout";
import Privacy from "./Privacy/Privacy";
import Refund from "./Refund/Refund";
import Terms from "./Terms/Terms";
import StripeTerms from "./Integrations/Stripe/Terms/StripeTerms";
import Navbar_ from "./Navbar/Navbar_";
import Banner from "./Banner/Banner";
import Sidebar from "./Sidebar/Sidebar";
import _Footer from "./Footer/_Footer";
import _Home from "./Home/_Home";
import EventTypeCreator from "./EventTypeCreator/_EventTypeCreator";
import Booking from "./BookingPage/Booking";
import BookingConfirmation from "./BookingConfirmation/BookingConfirmation";
import BookingAcceptReject from "./BookingAcceptReject/BookingAcceptReject";
import _Settings from "./Settings/_Settings";
import _Landing from "./Landing/_Landing";
import _PublicEvents from "./PublicEvents/_PublicEvents";
import Integrations from "./Integrations/_Integrations";
import WixIntegrationLanding from "./Integrations/Public/Wix/Wix";
import Embed from "./Embed/_Embed";
import Team from "./Team/_Team";
import TeamInvitePage from "./Team/InvitePage/InvitePage";
import Agency from "./Agency/Agency";
import InvitePage from "./Agency/InvitePage/InvitePage";
import Upgrade from "./Upgrade/Upgrade";
import CalendarConnections from "./CalendarConnections/_CalendarConnections";
import Onboarding from "./Onboarding/Onboarding";
import Leads from "./Leads/Leads";
import PageNotFound from "./Components/404/PageNotFound";
import ScrollToTop from "./Utilities/ScrollToTop";
import { useIframeStore } from "./store";
import {
  isIframe,
  setRootAsIframe,
  getIframeReferrer,
} from "./Utilities/iframeUtils";
import { Toaster } from "react-hot-toast";
import { initFlowbite } from "flowbite";
import auth from "./Account/Auth";

// Import the BE module used for backend calls:
import be from "./BE"; // adjust the relative path as needed
import Woztell from "./IFrames/Woztell";

// ─── COMPONENT: Analytics ─────────────────────────────────────────────
function Analytics() {
  const location = useLocation();

  useEffect(() => {
    // Retrieve user properties
    const userId = auth.getProperty({
      storageKey: "userSession",
      path: ["profileObj", "username"],
      fallback: null,
    });

    const userEmail = auth.getProperty({
      storageKey: "userSession",
      path: ["profileObj", "email"],
      fallback: null,
    });

    const userPlanStatus = auth.getProperty({
      storageKey: "userSession",
      path: ["profileObj", "planStatus", "status"],
      fallback: "free",
    });

    if (userId) {
      ReactGA.set({ user_id: userId });
      ReactGA.gtag("set", "user_properties", {
        email: userEmail,
        plan_status: userPlanStatus,
      });
    } else {
      ReactGA.set({ user_id: null });
      ReactGA.gtag("set", "user_properties", {});
    }

    // Send pageview info to GA
    ReactGA.send({
      hitType: "pageview",
      page: location.pathname + location.search,
      title: document.title,
    });
  }, [location]);

  return null;
}

// ─── COMPONENT: DiscountUpdater ───────────────────────────────────────
// This component will call the discount endpoint each time the route changes,
// but only if the current pathname matches "/" (extend the condition as needed).
function DiscountUpdater() {
  const location = useLocation();

  useEffect(() => {
    // Check if the current route is one where you want to update discounts.
    // For now, we check for the homepage ("/"). Extend this condition for other pages if needed.
    if (location.pathname === "/") {
      const fetchDiscounts = async () => {
        try {
          await auth
            .isAuthenticated()
            .then(() => {
              be.get(
                "payment-jubilant",
                "/payments/discounts",
                false,
                true,
                true
              )
                .then((response) => {
                  console.log("[payments][discounts] got discounts response:");
                  console.log(response);
                  // Save the response directly at the root level in local storage
                  // under the key "userDiscount" (i.e. no encapsulation in a subkey).
                  localStorage.setItem(
                    "userDiscount",
                    JSON.stringify(response)
                  );
                })
                .catch((error) => {
                  console.error(
                    "[payments][discounts] network error while fetching discounts:",
                    error
                  );
                });
            })
            .catch((error) => {
              console.error(
                "[payments][discounts] authentication error while fetching discounts:",
                error
              );
            });
        } catch (error) {
          console.error(
            "[payments][discounts] functional error while fetching discounts:",
            error
          );
        }
      };

      fetchDiscounts();
    }
  }, [location]);

  return null;
}

// ─── MAIN COMPONENT: App ───────────────────────────────────────────────
function App() {
  const isIframePage = isIframe();
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" });
  const isOnboarding = window.location.pathname === "/onboarding";
  const shouldShowNavs = useIframeStore((state) => state.shouldShowNavs);
  const setShouldShowNavs = useIframeStore((state) => state.setShouldShowNavs);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);

  const username = auth.getProperty({
    storageKey: "userSession",
    path: ["profileObj", "username"],
    fallback: "",
  });

  useEffect(() => {
    // Load Flowbite behaviours
    initFlowbite();

    // Logic to show/hide navbar and footer
    if (isIframePage) {
      if (getIframeReferrer() !== "wix-dashboard") {
        setRootAsIframe();
        setShouldShowNavs(false);
      } else if (getIframeReferrer() === "wix-dashboard") {
        setShouldShowNavs(true);
      }
    } else if (isOnboarding) {
      setShouldShowNavs(false);
    } else {
      setShouldShowNavs(true);
    }
  }, [shouldShowNavs]);

  return (
    <>
      <UserProvider>
        <Router>
          <ScrollToTop>
            <ThemeProvider>
              {/* Place DiscountUpdater here so it runs on every route change */}
              <DiscountUpdater />
              <div className="tw-flex tw-flex-col tw-min-h-screen">
                {shouldShowNavs && <Navbar_ />}
                <div className="tw-flex tw-flex-1 tw-m-0">
                  {shouldShowNavs && (
                    <Sidebar
                      isSidebarExpanded={isSidebarExpanded}
                      setIsSidebarExpanded={setIsSidebarExpanded}
                    />
                  )}
                  <main
                    className={`tw-flex-1 tw-transition-all tw-duration-300 ${
                      !username ||
                      window.location.pathname === "/onboarding" ||
                      window.location.pathname === "/woztell" ||
                      isMobile
                        ? "tw-ml-0"
                        : isSidebarExpanded
                        ? "tw-ml-64"
                        : "tw-ml-16"
                    }`}
                  >
                    <Banner />
                    <Analytics />
                    <Switch>
                      <Route exact path="/" component={_Home} />
                      <Route path="/login" component={_Landing} />
                      <Route path="/signin" component={SignIn} />
                      <Route path="/logout" component={Logout} />
                      <Route path="/onboarding" component={Onboarding} />
                      <Route path="/upgrade" component={Upgrade} />
                      <Route path="/settings" component={_Settings} />
                      <Route path="/privacy" component={Privacy} />
                      <Route path="/refund" component={Refund} />
                      <Route path="/terms" component={Terms} />
                      <Route path="/stripe-terms" component={StripeTerms} />
                      <Route
                        path="/eventcreator"
                        component={EventTypeCreator}
                      />
                      <Route
                        path="/eventeditor"
                        render={(props) => <EventTypeCreator {...props} />}
                      />
                      <Route
                        exact
                        path="/woztell"
                        render={(props) => <Woztell {...props} />}
                      />
                      <Route
                        exact
                        path="/integrations/wix"
                        component={WixIntegrationLanding}
                      />
                      <Route path="/integrations" component={Integrations} />
                      <Route path="/embed" component={Embed} />
                      <Route path="/leads" component={Leads} />
                      <Route
                        path="/calendarconnections"
                        component={CalendarConnections}
                      />
                      <Route exact path="/team" component={Team} />
                      <Route
                        exact
                        path="/team/invitee"
                        component={TeamInvitePage}
                      />
                      <Route exact path="/agency" component={Agency} />
                      <Route
                        exact
                        path="/agency/invitee"
                        component={InvitePage}
                      />
                      <Route exact path="/404" component={PageNotFound} />
                      <Route exact path="/:user" component={_PublicEvents} />
                      <Route
                        exact
                        path="/:user/:event"
                        render={(props) => <Booking {...props} mode="book" />}
                      />
                      <Route
                        exact
                        path="/operations/reschedule/:bookingid"
                        render={(props) => (
                          <Booking {...props} mode="reschedule" />
                        )}
                      />
                      <Route
                        exact
                        path="/operations/delete/:bookingid"
                        render={(props) => <Booking {...props} mode="delete" />}
                      />
                      <Route
                        exact
                        path="/operations/confirm/:bookingid"
                        render={(props) => <BookingConfirmation {...props} />}
                      />
                      <Route
                        exact
                        path="/b/accept/:bookingid"
                        render={(props) => <BookingAcceptReject {...props} />}
                      />
                      <Route
                        exact
                        path="/b/reject/:bookingid"
                        render={(props) => <BookingAcceptReject {...props} />}
                      />
                      <Route path="*" component={PageNotFound} />
                    </Switch>
                  </main>
                </div>
                {shouldShowNavs && <_Footer />}
              </div>
            </ThemeProvider>
          </ScrollToTop>
        </Router>
      </UserProvider>
      <Toaster />
    </>
  );
}

export default App;
